import { useState } from "react";
import { ArrowRight, ArrowLeft, ArrowLeftRight, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimeInput } from "@/components/TimeInput";
import { PLANTILLAS, FORMATOS } from "@/lib/carteles";
import { Cartel, PX_MM } from "@/components/carteles/CartelPlantilla";

// Fichas de las herramientas «Desvío» (dibujado a mano) y «Garajes»

const Opt = ({ on, onClick, icon: Icon, children, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md border text-xs ${on ? "border-green-600 bg-green-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
    <Icon className="w-4 h-4" />{children}
  </button>
);

// Desvío dibujado: sentido de las flechas (del primer nodo al último, al revés o en los dos sentidos)
export const DesvioForm = ({ el, onChange, readOnly }) => {
  const set = (patch) => !readOnly && onChange({ ...el, ...patch });
  const s = el.sentido || "ida";
  return (
    <div className="space-y-2" data-testid="manual-detour-form">
      <Label className="text-[0.6875rem] text-slate-500">Sentido del tráfico desviado (flechas)</Label>
      <div className="flex gap-1.5">
        <Opt on={s === "ida"} onClick={() => set({ sentido: "ida" })} icon={ArrowRight} disabled={readOnly} testid="manual-detour-ida">Ida</Opt>
        <Opt on={s === "vuelta"} onClick={() => set({ sentido: "vuelta" })} icon={ArrowLeft} disabled={readOnly} testid="manual-detour-vuelta">Vuelta</Opt>
        <Opt on={s === "ambos"} onClick={() => set({ sentido: "ambos" })} icon={ArrowLeftRight} disabled={readOnly} testid="manual-detour-ambos">Doble</Opt>
      </div>
      <div className="text-[0.625rem] text-slate-500 leading-snug">«Ida» va del primer nodo que pulsó al último. Arrastre los nodos para cambiar el trazado: se ajusta solo a las calles (curvas y rotondas incluidas).</div>
      {el.calles?.length > 0 && <div className="text-xs text-slate-700">{el.calles.join(" → ")}</div>}
      {el.rectos?.length > 0 && <div className="text-[0.6875rem] text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-2">Entre los nodos {el.rectos.map((i) => `${i + 1} y ${i + 2}`).join(", ")} no hay calle: el tramo queda recto. Acerque los nodos a la calle.</div>}
    </div>
  );
};

const RESTR = [["entrada", "Entrada"], ["salida", "Salida"], ["ambas", "Entrada y salida"]];

// Línea de garajes: texto del cartel, restricción, días y horas; los carteles A4/A3 salen debajo, ya rellenos
export const GarajesForm = ({ el, onChange, readOnly, ev }) => {
  const set = (k) => (e) => !readOnly && onChange({ ...el, [k]: e?.target ? e.target.value : e });
  const [formato, setFormato] = useState("A4");
  const varios = el.fecha_hasta && el.fecha_hasta !== el.fecha;
  const tpl = PLANTILLAS.find((t) => t.id === (varios ? "garaje_dias" : "garaje_dia"));
  const datos = { restriccion: el.restriccion || "entrada", fecha: el.fecha || ev?.date || "", fecha_hasta: el.fecha_hasta || el.fecha || ev?.date || "",
    hora: el.start_time || ev?.start_time || "", hora_hasta: el.end_time || ev?.end_time || "", motivo: ev?.name || "", dias: el.dias || "", texto_libre: el.texto_cartel || "" };
  const f = FORMATOS[formato];
  const ANCHO = 260;
  const escala = ANCHO / (f.w * PX_MM);
  const abrir = () => {
    const q = new URLSearchParams({ tpl: tpl.id, formato, ...Object.fromEntries(Object.entries(datos).filter(([, v]) => v)) });
    window.open(`/events/${ev.id}/carteles?${q}`, "_blank", "noopener");
  };
  return (
    <div className="space-y-3" data-testid="garages-form">
      <div>
        <Label className="text-[0.6875rem] text-slate-500">Se prohíbe</Label>
        <div className="flex gap-1.5 mt-1">
          {RESTR.map(([v, l]) => (
            <button key={v} type="button" disabled={readOnly} onClick={() => set("restriccion")(v)} data-testid={`garages-restr-${v}`}
              className={`flex-1 h-9 rounded-md border text-xs ${datos.restriccion === v ? "border-amber-500 bg-amber-400 text-slate-950 font-semibold" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[0.6875rem] text-slate-500">Día</Label><Input type="date" value={el.fecha || ev?.date || ""} onChange={set("fecha")} disabled={readOnly} className="h-9 mt-1" data-testid="garages-fecha" /></div>
        <div><Label className="text-[0.6875rem] text-slate-500">Hasta el día (opcional)</Label><Input type="date" value={el.fecha_hasta || ""} onChange={set("fecha_hasta")} disabled={readOnly} className="h-9 mt-1" data-testid="garages-fecha-hasta" /></div>
        <div><Label className="text-[0.6875rem] text-slate-500">Desde las</Label><TimeInput value={el.start_time || ""} onChange={set("start_time")} className="h-9 mt-1" data-testid="garages-desde" /></div>
        <div><Label className="text-[0.6875rem] text-slate-500">Hasta las</Label><TimeInput value={el.end_time || ""} onChange={set("end_time")} className="h-9 mt-1" data-testid="garages-hasta" /></div>
      </div>
      {varios && <div><Label className="text-[0.6875rem] text-slate-500">Solo los días (opcional)</Label><Input value={el.dias || ""} onChange={set("dias")} disabled={readOnly} placeholder="de lunes a viernes" className="h-9 mt-1" /></div>}
      <div>
        <Label className="text-[0.6875rem] text-slate-500">Texto del cartel</Label>
        <textarea value={el.texto_cartel || ""} onChange={set("texto_cartel")} disabled={readOnly} rows={3} maxLength={400} data-testid="garages-texto"
          placeholder={tpl.texto({ ...datos, texto_libre: "" })}
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-sky-500" />
        <div className="text-[0.625rem] text-slate-500">Vacío = el texto de la plantilla (en gris), con el día, las horas y el motivo del evento.</div>
      </div>
      <div className="space-y-1.5" data-testid="garages-posters">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-700 mr-auto">Cartel para los garajes</span>
          {Object.keys(FORMATOS).map((k) => (
            <button key={k} type="button" onClick={() => setFormato(k)} data-testid={`garages-format-${k}`}
              className={`h-8 px-3 rounded-md border text-xs font-semibold ${formato === k ? "border-sky-500 bg-sky-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{k}</button>
          ))}
        </div>
        <div className="overflow-hidden rounded border border-slate-300 bg-white pointer-events-none" style={{ width: ANCHO, height: f.h * PX_MM * escala }}>
          <div style={{ transform: `scale(${escala})`, transformOrigin: "top left" }}><Cartel tpl={tpl} datos={datos} formato={formato} ine={ev?.concello} /></div>
        </div>
        {ev?.id && <button type="button" onClick={abrir} className="flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid="garages-open"><Printer className="w-3.5 h-3.5" /> Abrir {formato} para imprimir</button>}
      </div>
    </div>
  );
};
